import React from 'react'
import { rtlConvertInline, css } from 'lib/paypalphrodite'
import {
  CaptionText,
  v2black,
  Tooltip,
  v2grey600,
  TextInput as TextInputV2,
  BodyText,
  Button,
  Badge,
  v2blue400,
  v2green400,
  DropdownMenu,
} from '@paypalcorp/pp-react'
import { post } from '../../calculator/lib/xhr'
import styles from './selected-funding-option.styles'
import Separator from './separator'
import classnames from 'classnames'
import griffinNumber from 'griffin-paypal/number'
import {
  cardSpecTypesToCardTypeConstants,
  WALLET,
  OPEN_BANKING_FLOWS,
  FLOW_TYPES,
} from '../constants'
import {
  INSTRUMENT_TYPES,
  REVIEW_WITH_SELLER_FEE_VARIANTS,
  REVIEW_WITH_FEE_TOOLTIP,
  PP_CREDIT_PROMO_STATE,
} from '../../../../lib/constants'
import enRTRStrings from '../../../../config/enRTRStrings'
import * as ViewActions from '../actions/Actions'
import * as OverpanelActions from '../actions/overpanels'
import { setFocusToHtmlElement } from '../utils/preview-utils'
import { getFILinkName, generateFIFlowUrl } from '../../experiments/fiFlow'
import { isBFXCurrencyConversionTrmt } from '../../experiments/bfx-currency-conversion'
import { hasAddFiPostMigration } from '../../experiments/migrateRoutes'
import ChangeTextOrIcon from './preview/change-text-icon'
import TooltipTarget from '../containers/vx-tooltip/target'
import { trackCashAdvanceFeesClick } from '../utils/cash-advance-fees-analytics'
import OpenBankingWarning from './open-banking/openBankingWarning'

/**
 * IMPORTANT this component exists to support users ENROLLED in the consumer choice experiment.
 * Because many of the choice changes will be legally required shortly, we should delete the older component
 * and start using this 100% soon. Until then we'll be using selected-funding-option.js
 * and pxp to load this NEWER component when necessary.
 */

/**
 * Displays the selected funding option on the preview pages of the send, buy, gift,
 * and pay request flows. This component:
 * - allows user to open up funding options sidepanel to change selected funding option
 * - shows currency conversion info
 */

import PropTypes from 'prop-types'

import { genericPropType } from 'prop-validators'
import TextInput from 'react-text-input'
import _ from 'lodash'
import {
  buildFundingSourceDisplayName,
  buildFundingSourceDisplayNameENRTR,
  generateSourceLogoUrls,
  buildFundingSourceAccountType,
  getPPCreditSpecialOfferText,
} from '../utils/funding-option-utils'
import { trackImpression, trackLink } from '../../lib/analytics/fpti'
import glamorous from 'glamorous'
import content from 'pp-react-l10n'
import CashAdvanceFeesWarning from './cash-advance-fees-overpanel/cash-advance-fees-warning'
import { connect } from 'transfer/utils/p2p-connect'
import { tracking } from '../../lib/analytics/mParticle'
import Events from '../../lib/analytics/eventTags'
import { getTrafficSource } from '../utils/mgmUtils'
import { isDw2Repaint } from 'experiments/dw2Repaint'
import { BOX_BORDER_STYLES } from 'lib/styles/styles'
import serverProps from '../utils/serverProps'
import SheetTooltip from './sheet-tooltip'

let i18n = content('transfer/inc/selectedFundingOption')
let i18nRTR = content('transfer/inc/rtr')
const CDN_HOST_NAME = _.get(serverProps, 'cdnHostName')

const Wrapper = glamorous.div({
  ...(isDw2Repaint() && {
    marginBottom: '1rem',
    ...BOX_BORDER_STYLES,
  }),
})

const SendingWithDiv = glamorous.div(
  {
    color: '#000000',
    display: 'flex',
    justifyContent: 'space-between',
    fontFamily: '"PayPalSansSmall-Regular", Helvetica Neue, Arial, sans-serif',
    alignItems: 'center',
    marginTop: '5px',
  },
  ({ showENSubTextRTR }) => {
    if (showENSubTextRTR) {
      return {
        alignItems: 'flex-start',
      }
    }
  }
)

const ToggleButton = glamorous.button('vx_btn-link', {
  cursor: 'pointer',
})

const Convertor = glamorous.div(
  {
    padding: isDw2Repaint() ? '0.5rem' : '3px 16px 8px 16px',
    color: isDw2Repaint() ? v2black : '#000000',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: isDw2Repaint() ? 'baseline' : 'center',
  },
  ({ showENSubTextRTR }) => {
    if (showENSubTextRTR) {
      return {
        alignItems: 'flex-start',
      }
    }
  }
)

const YouWillSendDiv = glamorous.div(
  {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: isDw2Repaint() ? '0.5rem' : '20px 16px 10px',
    color: isDw2Repaint() ? v2black : '#000000',
  },
  ({ showENSubTextRTR }) => {
    if (showENSubTextRTR) {
      return {
        alignItems: 'flex-start',
      }
    }
  }
)

const SelectedFundingOptionSendDiv = glamorous.div({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0.5rem',
})

const FeesRow = glamorous.div(
  {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: isDw2Repaint() ? v2black : '#000000',
    padding: isDw2Repaint() ? '0.5rem' : '0 2px 12px',
    margin: isDw2Repaint() ? 0 : 5,
  },
  ({ showENSubTextRTR }) => {
    if (showENSubTextRTR) {
      return {
        alignItems: 'flex-start',
      }
    }
  }
)

const FirstTimeUserHelpInfoContent = glamorous.div(
  {
    padding: '0.5rem',
    '& a': {
      fontWeight: 600,
      color: v2blue400,
    },
  },
  ({ showFirstTimeUserHelpInfo }) => {
    return {
      display: showFirstTimeUserHelpInfo ? 'block' : 'none',
    }
  }
)

const SenderFeeContentWithTooltip = glamorous.div({
  display: 'flex',
  alignItems: 'baseline',
  '& button': {
    backgroundColor: 'white',
  },
  '& .vx_text-legal': {
    fontFamily: 'PayPalSansBig-Regular, "Helvetica Neue", Arial, sans-serif',
    '& a': {
      color: v2blue400,
      fontFamily: 'PayPalSansBig-Regular, "Helvetica Neue", Arial, sans-serif',
      fontWeight: 600,
    },
  },
})

const PreferredBadge = glamorous.div({
  borderRadius: '2px',
  backgroundColor: 'rgba(0, 207, 146, 0.1)',
  marginLeft: 60,
  padding: '1px 8px',
  display: 'inline-block',
})

const PreferredText = glamorous.div('ppvx_text--xl ppvx_text--regular', {
  fontSize: '10px',
  color: '#006447',
  margin: '0px',
})

const StateMandatedWarning = glamorous.div({
  margin: '1rem 0',
  padding: '0 0.4375rem',
  ...(!isDw2Repaint() && { color: v2black }),
})
const StateMandatedWarningPrimaryText = glamorous.div(
  ({ showENSubTextRTR = false }) => ({
    ...(showENSubTextRTR && { marginBottom: '0.5rem' }),
  })
)
const StateMandatedWarningEnRtrText = glamorous.div({
  color: v2grey600,
})

const FundingSourceWrapper = glamorous.div({
  display: 'flex',
  justifyContent: 'space-between',
})

const FundingSourceTitleLogoWrapper = glamorous.div({
  display: 'flex',
  alignItems: 'flex-start',
})

const CreditSpecialOfferInfoText = glamorous.div({
  '& a': {
    color: v2blue400,
    fontFamily: 'PayPalSansBig-Medium, Helvetica Neue, Arial, sans-serif',
  },
})

const SendingWithDivWrapper = glamorous.div({
  display: 'flex',
  justifyContent: 'space-between',
  paddingTop: '0.5rem',
})

const CurrencyConversionLinkDescriptionDiv = glamorous.div({
  display: 'flex',
  padding: '0 0.5rem 1rem 0.5rem',
  justifyContent: 'space-between',
})

const AdditionalDisclosureActionsDiv = glamorous.div({
  paddingLeft: '0.5rem',
  paddingBottom: '1rem',
})

const AddFundingOptionLinkDiv = glamorous.div({
  textAlign: 'center',
  margin: '1rem 2rem 2rem',
})

const PayPalFeeLabelDiv = glamorous.div({
  display: 'flex',
  alignItems: 'center',
})

export class SelectedFundingOption extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    fundingOptions: PropTypes.array.isRequired,
    overpanelType: PropTypes.string,
    selectedFundingOption: PropTypes.shape({
      isFeeWaived: PropTypes.bool,
      isRTR: PropTypes.bool,
      feePaidByReceiver: PropTypes.bool,
      currency_exchange: PropTypes.object,
      showFeeIsIncluded: PropTypes.bool,
      funding_sources: PropTypes.array,
      installPlan: PropTypes.array,
      displayAmounts: PropTypes.shape({
        fee: PropTypes.string,
        paid: PropTypes.string,
        sent: PropTypes.string,
      }),
      showCvv: PropTypes.bool,
      isFirstFeeFree: PropTypes.bool,
    }),
    isRTR: PropTypes.bool,
    selectedInstallmentPlan: PropTypes.object,
    shouldShowCurrencyConversionLink: PropTypes.bool.isRequired,
    reviewInfoComponent: genericPropType,
    firstTimeUserHelpInfoContent: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]).isRequired,
    currencyConversionComponent: genericPropType,
    cardSpecs: PropTypes.object,
    showDonateContent: PropTypes.bool,
    showCondensedUI: PropTypes.bool,
    flowType: PropTypes.string,
    cvv: PropTypes.string,
    isRecurring: PropTypes.bool,
    conversionType: PropTypes.string,
    payerId: PropTypes.string,
    recipient: PropTypes.string,
    token: PropTypes.string,
    displayName: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    networkId: PropTypes.string,
    amount: PropTypes.string,
    currencyCode: PropTypes.string,
    fromCurrencyCode: PropTypes.string,
    env: PropTypes.string,
    isCFPBEnabledCountry: PropTypes.bool,
    userAccountType: PropTypes.string,
    isCardArtEnabled: PropTypes.bool,
    addCardSupported: PropTypes.bool,
    unilateralName: PropTypes.string,
    unilateralCountryCode: PropTypes.string,
    cashAdvanceWarningContent: PropTypes.string,
    unavailableFI: PropTypes.bool,
    additionalDisclosureActions: PropTypes.array,
    countryCode: PropTypes.string,
    mayCashAdvanceFeesApply: PropTypes.bool,
    isPurchase: PropTypes.bool,
    language: PropTypes.string,
    isMgm: PropTypes.bool,
    griffinMetadata: PropTypes.object.isRequired,
    shouldShowFundingOptionsOverpanel: PropTypes.bool,
    flowIntent: PropTypes.string,
    showStateMandatedWarning: PropTypes.bool,
    note: PropTypes.string,
    isDonate: PropTypes.bool,
    shopsInformation: PropTypes.object,
    isSingleProductShops: PropTypes.bool,
  }

  static defaultProps = {
    payerId: '',
    showStateMandatedWarning: false,
  }

  state = {
    paypalBalances: [],
    showFirstTimeUserHelpInfo: false,
  }

  componentWillMount() {
    const {
      selectedFundingOption: { funding_sources = [] },
    } = this.props
    this.setPayPalBalancesToState(funding_sources)
  }

  componentDidMount() {
    setFocusToHtmlElement(this._fundingOption)
  }

  componentWillReceiveProps(newProps) {
    const {
      selectedFundingOption: { funding_sources = [] },
    } = newProps
    this.setPayPalBalancesToState(funding_sources)
  }

  handleToggleFundingOptions = event => {
    event.preventDefault()
    const analyticsPageGroup = `main:p2p:unified:${this.props.flowType}::review`
    const analyticsPageName = `main:p2p:unified:${this.props.flowType}::review:node::`
    trackLink('changeFI', analyticsPageGroup, analyticsPageName, 'link')
    this.props.dispatch(
      OverpanelActions.toggleFundingOptionsOverpanel({ show: true })
    )
  }

  setPayPalBalancesToState = fundingSources => {
    const paypalBalances = fundingSources
      .filter(source => source.instrument_type === INSTRUMENT_TYPES.HOLDING)
      .map(source => source.total_funds_in)
    this.setState({ paypalBalances })
  }

  handleToggleAddCardOverpanel = event => {
    event.preventDefault()
    // FPTI Click Impression Tracking
    const linkName = getFILinkName(this.props.flowType)
    const analyticsPageName =
      linkName === WALLET.NEW_BANK
        ? `main:consumer:p2p:${this.props.flowType}:${linkName}:${this.props.env}`
        : `main:consumer:p2p:${this.props.flowType}:noFI`
    trackLink(linkName, analyticsPageName, analyticsPageName, 'link')

    const isIframe = window.top !== window
    const mainWindow = isIframe ? window.top : window
    const fiFlowPayload = {
      flowType: this.props.flowType,
      amount: this.props.amount,
      currencyCode: this.props.currencyCode,
      payerId: this.props.payerId,
      recipient: this.props.recipient,
      obFlow: OPEN_BANKING_FLOWS.ADD_BANK,
      unilateralName: this.props.unilateralName,
      unilateralCountryCode: this.props.unilateralCountryCode,
      mayCashAdvanceFeesApply: this.props.mayCashAdvanceFeesApply,
      isPurchase: this.props.isPurchase,
      isMgm: this.props.isMgm,
      mgmTrafficSource: getTrafficSource(),
      displayName: this.props.displayName,
      thumbnailUrl: this.props.thumbnailUrl,
      networkId: this.props.networkId,
      flowIntent: this.props.flowIntent,
      note: this.props.note,
      token: this.props.token,
      ...this.props.shopsInformation,
    }
    const fiFlowUrl = generateFIFlowUrl(fiFlowPayload)
    const fiNewUrl = '/myaccount/transfer/flow/fi/new'
    if (hasAddFiPostMigration()) {
      post(fiNewUrl, fiFlowPayload)
        .then(response => {
          const { redirectURL = {} } = response
          mainWindow.location.assign(redirectURL)
        })
        .catch(err => {
          return this.props.dispatch(ViewActions.showError(err))
        })
    } else {
      mainWindow.location.assign(fiFlowUrl)
    }
  }

  // Check if user is allowed to add a FI
  // 1. If user is not in pisp environment, we assume they can add FI
  // 2. If user is in pisp environment, also check if addCardSupported from paymentExperienceContext
  shallAllowAddFI = () => {
    const { env, addCardSupported } = this.props
    return env !== 'pisp' || addCardSupported
  }

  renderAddFundingOptionLink = () => {
    if (!this.shallAllowAddFI()) return null

    const linkName = getFILinkName(this.props.flowType)
    const linkDisplayText =
      linkName === WALLET.NEW_BANK
        ? i18n('link.addBank')
        : i18n('link.noSelectedFI')
    const analyticsPageName =
      linkName === WALLET.NEW_BANK
        ? `main:consumer:p2p:${this.props.flowType}:${linkName}:${this.props.env}`
        : `main:consumer:p2p:${this.props.flowType}:noFI`

    trackImpression(analyticsPageName, analyticsPageName)

    return isDw2Repaint() ? (
      <AddFundingOptionLinkDiv id="noFI_addFI" data-nemo="noFI_addFI">
        <Button
          tertiary={true}
          type="button"
          style={{ padding: 0 }}
          data-nemo="add-fi-btn"
          onClick={this.handleToggleAddCardOverpanel}
        >
          {linkDisplayText}
        </Button>
      </AddFundingOptionLinkDiv>
    ) : (
      <span
        id="noFI_addFI"
        data-nemo="noFI_addFI"
        className="selectedFundingOption-details nofi-selected animated"
      >
        <button
          type="button"
          className="vx_btn-link"
          data-nemo="add-fi-btn"
          onClick={this.handleToggleAddCardOverpanel}
        >
          {linkDisplayText}
        </button>
      </span>
    )
  }

  handleShowCashAdvanceFeeOverpanel = () => {
    event.preventDefault()
    tracking(
      Events.p2p_fi_selection_warning_pressed({
        fi_warning_type: 'cash_advance',
      })
    )
    trackCashAdvanceFeesClick('previewInlineMoreInfo')
    this.props.dispatch(
      OverpanelActions.toggleCashAdvanceFeesOverpanel({
        show: true,
        type: 'ABOUT',
        selectedFundingOption: this.props.selectedFundingOption,
      })
    )
  }

  handleFirstTimeUserTooltipClick = () => {
    this.setState(
      {
        showFirstTimeUserHelpInfo: !this.state.showFirstTimeUserHelpInfo,
      },
      () => {
        const display = this.state.showFirstTimeUserHelpInfo ? 'open' : 'close'
        const pageName = `main:consumer:p2p:send:FI:${display}`
        trackImpression(pageName, pageName)
      }
    )
  }

  handleCVVChange = event => {
    this.props.dispatch(ViewActions.changeCVVCode(event.target.value))
  }

  renderFeesRow = () => {
    const {
      selectedFundingOption: {
        isRTR,
        feePaidByReceiver,
        displayAmounts: { fee },
        isFeeWaived,
        isFirstFeeFree,
      },
      countryCode,
      language,
      isDonate,
    } = this.props
    if (!isFirstFeeFree && (feePaidByReceiver || isFeeWaived)) {
      return null
    }
    const feeContentKey = isFirstFeeFree || fee ? 'copy.feeText' : 'copy.free'
    const firstTimeUserHelpContentWrapperClasses = css(
      styles.firstTimeUserHelpInfo,
      this.state.showFirstTimeUserHelpInfo && styles.firstTimeUserHelpInfoEnter
    )
    const showENSubTextRTR = isRTR === true && language !== 'en'
    const feeContentClassNames = classnames(
      'txtBold',
      css(styles.rtrPrimaryTextSmall),
      {
        [css(styles.rtrPrimaryText)]: showENSubTextRTR,
      }
    )

    if (
      isBFXCurrencyConversionTrmt() ||
      REVIEW_WITH_FEE_TOOLTIP.includes(this.props.overpanelType)
    ) {
      const feeTooltipClass = classnames(
        'ppvx_icon ppvx_icon--size_xs ppvx_icon-info',
        css(styles.feeInfoTooltip)
      )
      return isDw2Repaint() ? (
        <FeesRow showENSubTextRTR={showENSubTextRTR}>
          <div data-nemo="sender-fee-content">
            <SenderFeeContentWithTooltip>
              <BodyText>{i18n(feeContentKey)}</BodyText>
              <TooltipTarget
                content={
                  isFeeWaived
                    ? i18n('fee.tooltip.noFee')
                    : i18n.html('fee.tooltip.fee', { countryCode })
                }
                isClickable={true}
                neverShowBubble={true}
                handleClick={this.handleFirstTimeUserTooltipClick}
                iconClasses={feeTooltipClass}
                horizontalPosition="left"
              />
            </SenderFeeContentWithTooltip>
            {showENSubTextRTR && (
              <CaptionText style={{ color: v2grey600 }}>
                {enRTRStrings[feeContentKey]}
              </CaptionText>
            )}
          </div>
          <BodyText>{fee}</BodyText>
        </FeesRow>
      ) : (
        <FeesRow showENSubTextRTR={showENSubTextRTR}>
          <div className="ppvx_text--md" data-nemo="sender-fee-content">
            <span
              className={classnames({
                [css(styles.rtrPrimaryText)]: showENSubTextRTR,
              })}
            >
              {i18n(feeContentKey)}
            </span>
            <TooltipTarget
              content={
                isFeeWaived
                  ? i18n('fee.tooltip.noFee')
                  : i18n.html('fee.tooltip.fee', { countryCode })
              }
              isClickable={true}
              neverShowBubble={true}
              handleClick={this.handleFirstTimeUserTooltipClick}
              iconClasses={feeTooltipClass}
              horizontalPosition="left"
            />
            {showENSubTextRTR && (
              <div className={css(styles.rtrENSubText)}>
                {enRTRStrings[feeContentKey]}
              </div>
            )}
          </div>
          <div className="ppvx_text--md" data-nemo="sender-fee">
            <span className={classnames(css(styles.rtrPrimaryTextSmall))}>
              {fee}
            </span>
          </div>
        </FeesRow>
      )
    }

    return isDw2Repaint() ? (
      <div>
        <FeesRow>
          <PayPalFeeLabelDiv>
            <BodyText>{i18n(feeContentKey)}</BodyText>
            {showENSubTextRTR && (
              <CaptionText style={{ color: v2grey600 }}>
                {enRTRStrings[feeContentKey]}
              </CaptionText>
            )}
            {isDonate && (
              <SheetTooltip
                title={i18n('fee.tooltip.aboutOurFees')}
                description={
                  isFeeWaived
                    ? i18n('fee.tooltip.noFee')
                    : i18n('fee.tooltip.standardFee')
                }
                linkText={i18n('fee.tooltip.learnMore')}
                linkHref="/webapps/mpp/paypal-fees"
              />
            )}
          </PayPalFeeLabelDiv>
          {isFirstFeeFree ? (
            <div>
              <BodyText strong style={{ color: v2green400 }}>
                {i18n('copy.noFeeText')}
              </BodyText>
              {showENSubTextRTR && (
                <CaptionText style={{ color: v2grey600 }}>
                  {enRTRStrings['copy.noFeeText']}
                </CaptionText>
              )}
            </div>
          ) : (
            <BodyText>{fee}</BodyText>
          )}
        </FeesRow>
        <CaptionText
          as={FirstTimeUserHelpInfoContent}
          showFirstTimeUserHelpInfo={this.state.showFirstTimeUserHelpInfo}
        >
          {this.props.firstTimeUserHelpInfoContent}
        </CaptionText>
      </div>
    ) : (
      <div className="selectedFundingOption-fees">
        <div className={css(styles.clearfix)}>
          <div className="row">
            <div className="col-xs-6">
              <div
                className={feeContentClassNames}
                data-nemo="sender-fee-content"
              >
                {i18n(feeContentKey)}
              </div>
              {showENSubTextRTR && (
                <div className={css(styles.rtrENSubText)}>
                  {enRTRStrings[feeContentKey]}
                </div>
              )}
            </div>
            {isFirstFeeFree ? (
              <div className="col-xs-6 txtRight" data-nemo="sender-fee">
                <div
                  className={classnames(
                    'test_payer-fee txtBold',
                    css(styles.rtrPrimaryTextSmall),
                    css(styles.feeWaived)
                  )}
                >
                  {i18n('copy.noFeeText')}
                </div>
                {showENSubTextRTR && (
                  <div className={css(styles.rtrENSubText)}>
                    {enRTRStrings['copy.noFeeText']}
                  </div>
                )}
              </div>
            ) : (
              <div
                className="col-xs-6 txtRight test_payer-fee"
                data-nemo="sender-fee"
              >
                <span className={classnames(css(styles.rtrPrimaryTextSmall))}>
                  {fee}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className={firstTimeUserHelpContentWrapperClasses}>
          <div className="vx_alert vx_alert-help">
            <p className="vx_alert-text">
              {this.props.firstTimeUserHelpInfoContent}
            </p>
          </div>
        </div>
      </div>
    )
  }

  renderSendingAmountRow = () => {
    const {
      selectedFundingOption: {
        isRTR,
        feePaidByReceiver,
        displayAmounts: { paid, sent },
      },
      language,
      showDonateContent,
      env,
      isSingleProductShops,
    } = this.props
    const labelSendText =
      env === FLOW_TYPES.PPSHOPS ? 'label.subtotalText' : 'label.sendText'
    const sentContent = showDonateContent
      ? i18n('sendDetail.youllDonate')
      : i18n(labelSendText)
    const showENSubTextRTR = isRTR === true && language !== 'en'
    const rtrSentContentClassName = classnames(
      'txtBold',
      css(styles.rtrPrimaryTextSmall),
      {
        [css(styles.rtrPrimaryText)]: showENSubTextRTR,
      }
    )
    // show the message when
    // 1. transaction is rtr
    // 2. sender needs to pay a fee
    // 3. It is not a single product PayPal shops payment
    if (
      !REVIEW_WITH_SELLER_FEE_VARIANTS.includes(this.props.overpanelType) &&
      (isRTR || (sent !== paid && !feePaidByReceiver)) &&
      !isSingleProductShops
    ) {
      if (isBFXCurrencyConversionTrmt()) {
        return (
          <YouWillSendDiv showENSubTextRTR={showENSubTextRTR}>
            {isDw2Repaint() ? (
              <>
                <div>
                  <BodyText data-nemo="you-will-send-content">
                    {sentContent}
                  </BodyText>
                  {showENSubTextRTR && (
                    <CaptionText style={{ color: v2grey600 }}>
                      {enRTRStrings[labelSendText]}
                    </CaptionText>
                  )}
                </div>
                <BodyText data-nemo="you-will-send-fee">{sent}</BodyText>
              </>
            ) : (
              <>
                <div>
                  <div
                    className={classnames('ppvx_text--md', {
                      [css(styles.rtrPrimaryText)]: showENSubTextRTR,
                    })}
                    data-nemo="you-will-send-content"
                  >
                    {sentContent}
                  </div>
                  {showENSubTextRTR && (
                    <div className={css(styles.rtrENSubText)}>
                      {enRTRStrings[labelSendText]}
                    </div>
                  )}
                </div>
                <div className="ppvx_text--md" data-nemo="you-will-send-fee">
                  <span className={classnames(css(styles.rtrPrimaryTextSmall))}>
                    {sent}
                  </span>
                </div>
              </>
            )}
          </YouWillSendDiv>
        )
      }
      return isDw2Repaint() ? (
        <SelectedFundingOptionSendDiv>
          <div>
            <BodyText data-nemo="you-will-send-content">{sentContent}</BodyText>
            {showENSubTextRTR && (
              <CaptionText style={{ color: v2grey600 }}>
                {enRTRStrings[labelSendText]}
              </CaptionText>
            )}
          </div>
          <BodyText data-nemo="you-will-send-fee">{sent}</BodyText>
        </SelectedFundingOptionSendDiv>
      ) : (
        <div className="selectedFundingOption-send">
          <div className="row">
            <div className="col-xs-6">
              <div
                className={rtrSentContentClassName}
                data-nemo="you-will-send-content"
              >
                {sentContent}
              </div>
              {showENSubTextRTR && (
                <div className={css(styles.rtrENSubText)}>
                  {enRTRStrings[labelSendText]}
                </div>
              )}
            </div>
            <div
              className="col-xs-6 txtRight test_payer-fee"
              data-nemo="you-will-send-fee"
            >
              <span className={classnames(css(styles.rtrPrimaryTextSmall))}>
                {sent}
              </span>
            </div>
          </div>
        </div>
      )
    }
  }

  getPaymentCardType = source => {
    let { network = '' } = source
    return cardSpecTypesToCardTypeConstants[network.toLowerCase()]
  }

  renderPreferredBadge = () => {
    if (isBFXCurrencyConversionTrmt()) {
      return isDw2Repaint() ? (
        <Badge
          type="feature"
          style={{ marginTop: '0.5rem' }}
          data-nemo="preferred-badge"
        >
          {i18n('badge.preferred')}
        </Badge>
      ) : (
        <PreferredBadge data-nemo="preferred-badge">
          <PreferredText>{i18n('label.preferred')}</PreferredText>
        </PreferredBadge>
      )
    }
    return isDw2Repaint() ? (
      <Badge
        type="feature"
        style={{ marginTop: '0.5rem' }}
        data-nemo="preferred-badge"
      >
        {i18n('badge.preferred')}
      </Badge>
    ) : (
      <div data-nemo="preferred-badge" className={css(styles.preferredBadge)}>
        <p className={`vx_text-6_lighter ${css(styles.preferredBadgeText)}`}>
          {i18n('label.preferred')}
        </p>
      </div>
    )
  }

  renderFundingSourceAmount = amount => {
    if (isBFXCurrencyConversionTrmt()) {
      return isDw2Repaint() ? (
        <BodyText data-nemo="funding-source-amount">{amount}</BodyText>
      ) : (
        <span
          className={`ppvx_text--md col-xs-3 txtRight ${css(
            rtlConvertInline(styles.fundingSourceAmount)
          )}`}
          style={{ color: '#000000' }}
          data-nemo="funding-source-amount"
        >
          {amount}
        </span>
      )
    }
    return isDw2Repaint() ? (
      <BodyText data-nemo="funding-source-amount">{amount}</BodyText>
    ) : (
      <span
        className={`col-xs-3 txtRight ${css(
          rtlConvertInline(styles.fundingSourceAmount)
        )}`}
        data-nemo="funding-source-amount"
      >
        {amount}
      </span>
    )
  }

  renderFundingSources = () => {
    const {
      selectedFundingOption: { funding_sources, isRTR, displayAmounts },
      isCFPBEnabledCountry,
      userAccountType,
      language,
      isDonate,
    } = this.props

    const showENSubTextRTR = isRTR === true && language !== 'en'

    return funding_sources.map((source, i) => {
      const { isPreferred, total_funds_in, thumbnail_logo_url } = source
      let { amount } = total_funds_in
      if (isDonate && displayAmounts && displayAmounts.sent) {
        amount = displayAmounts.sent
      }
      const title = buildFundingSourceDisplayName(
        source,
        isCFPBEnabledCountry,
        userAccountType,
        this.props.isCardArtEnabled
      )
      const titleENSubTextRTR = buildFundingSourceDisplayNameENRTR(
        source,
        isCFPBEnabledCountry,
        userAccountType,
        this.props.isCardArtEnabled
      )
      const accountType = buildFundingSourceAccountType(source)
      const accountTypeENSubTextRTR = buildFundingSourceAccountType(
        source,
        showENSubTextRTR
      )
      const creditSpecialOfferText = getPPCreditSpecialOfferText(source)
      const showOfferEligibilityText =
        source.ppcreditPromoFinancingOffer === PP_CREDIT_PROMO_STATE.ELIGIBLE ||
        source.ppcreditPromoFinancingOffer === PP_CREDIT_PROMO_STATE.INELIGIBLE

      const accountTypeClasses = classnames(
        'vx_text-body_secondary',
        css(styles.fundingSecondaryText, styles.preventIncorrectTitleWrap),
        { [css(styles.rtrAccountType)]: showENSubTextRTR }
      )
      const accountTypeENSubTextRTRClasses = classnames(
        'vx_text-body_secondary',
        css(styles.fundingSecondaryText, styles.preventIncorrectTitleWrap),
        { [css(styles.rtrAccountTypeENSubText)]: showENSubTextRTR }
      )
      const specialOfferTextClasses = classnames(
        'vx_text-body_secondary',
        css(
          styles.fundingSecondaryText,
          styles.preventIncorrectTitleWrap,
          styles.ppcreditSpecialOfferText,
          showOfferEligibilityText && styles.offerEligibilityText
        )
      )
      const cardArtClasses = classnames(
        css(rtlConvertInline(styles.fundingSourceLogo)),
        {
          [css(styles.choiceCardArt)]:
            this.props.isCardArtEnabled && thumbnail_logo_url,
        }
      )

      const fundingSourceWrapperClasses = isBFXCurrencyConversionTrmt()
        ? {}
        : css(rtlConvertInline(styles.fundingSourceWrapper))

      return (
        <React.Fragment key={`fundingSource_${i}`}>
          {isDw2Repaint() ? (
            <>
              <FundingSourceWrapper>
                <FundingSourceTitleLogoWrapper>
                  <img
                    className={cardArtClasses}
                    src={generateSourceLogoUrls(
                      source,
                      isCFPBEnabledCountry,
                      userAccountType,
                      this.props.isCardArtEnabled
                    )}
                    alt=""
                    width={50}
                  />
                  <div>
                    <BodyText data-nemo={`selected-funding-source-title-${i}`}>
                      {title}
                    </BodyText>
                    {showENSubTextRTR && title !== titleENSubTextRTR && (
                      <CaptionText style={{ color: v2grey600 }}>
                        {titleENSubTextRTR}
                      </CaptionText>
                    )}
                    {creditSpecialOfferText && (
                      <CaptionText
                        as={CreditSpecialOfferInfoText}
                        data-nemo={`paypal-credit-promo-content`}
                        {...creditSpecialOfferText}
                      />
                    )}
                    {this.props.isCardArtEnabled && (
                      <>
                        <CaptionText>{accountType}</CaptionText>
                        {showENSubTextRTR && (
                          <CaptionText style={{ color: v2grey600 }}>
                            {accountTypeENSubTextRTR}
                          </CaptionText>
                        )}
                      </>
                    )}
                    {isPreferred && this.renderPreferredBadge()}
                  </div>
                </FundingSourceTitleLogoWrapper>
                {this.renderFundingSourceAmount(amount)}
              </FundingSourceWrapper>
              {this.props.flowType === 'send' &&
                source.cashAdvanceFeeApplies && (
                  <CashAdvanceFeesWarning
                    stylesOverride={{ marginTop: '1rem' }}
                    messageOverride={this.props.cashAdvanceWarningContent}
                    handleOnClick={this.handleShowCashAdvanceFeeOverpanel}
                  />
                )}
            </>
          ) : (
            <>
              <div className={`row ${fundingSourceWrapperClasses}`}>
                <div
                  className={`col-xs-9 ${css(
                    rtlConvertInline(styles.fundingSourceTitleLogoWrapper)
                  )}`}
                >
                  <img
                    className={cardArtClasses}
                    src={generateSourceLogoUrls(
                      source,
                      isCFPBEnabledCountry,
                      userAccountType,
                      this.props.isCardArtEnabled
                    )}
                    width={50}
                    alt=""
                  />
                  <p
                    className={classnames(
                      css(
                        rtlConvertInline(styles.fundingSourceTitle),
                        styles.preventIncorrectTitleWrap
                      ),
                      {
                        [css(styles.rtrFundingSourceTitle)]:
                          showENSubTextRTR && title !== titleENSubTextRTR,
                      }
                    )}
                    data-nemo={`selected-funding-source-title-${i}`}
                  >
                    {title}
                  </p>
                  {showENSubTextRTR && title !== titleENSubTextRTR && (
                    <p
                      className={classnames(
                        css(
                          rtlConvertInline(styles.fundingSourceTitle),
                          styles.preventIncorrectTitleWrap
                        ),
                        css(styles.rtrFundingSourceTitleENSubText)
                      )}
                    >
                      {titleENSubTextRTR}
                    </p>
                  )}
                  {creditSpecialOfferText && (
                    <p
                      className={specialOfferTextClasses}
                      data-nemo={`paypal-credit-promo-content`}
                    >
                      <span {...creditSpecialOfferText} />
                    </p>
                  )}
                  {this.props.isCardArtEnabled && (
                    <>
                      <p className={accountTypeClasses}>{accountType}</p>
                      {showENSubTextRTR && (
                        <p className={accountTypeENSubTextRTRClasses}>
                          {accountTypeENSubTextRTR}
                        </p>
                      )}
                    </>
                  )}
                  {isPreferred && this.renderPreferredBadge()}
                </div>

                {this.renderFundingSourceAmount(amount)}
              </div>
              {this.props.flowType === 'send' &&
                source.cashAdvanceFeeApplies && (
                  <CashAdvanceFeesWarning
                    stylesOverride={{ margin: '1rem auto 1rem auto' }}
                    messageOverride={this.props.cashAdvanceWarningContent}
                    handleOnClick={this.handleShowCashAdvanceFeeOverpanel}
                  />
                )}
            </>
          )}
        </React.Fragment>
      )
    })
  }

  renderDropdownOptions = () => {
    const {
      selectedFundingOption: { installPlan = [] },
    } = this.props
    return installPlan.map(installmentPlan => {
      const {
        paymentTimes,
        paymentAmount,
        discountPercent = '',
      } = installmentPlan

      const discount = Number(discountPercent) / 100
      const griffinNumberHandler = griffinNumber.createHandler(
        this.props.griffinMetadata
      )

      const normalizedAmount = griffinNumberHandler.formatNumber(discount, {
        style: 'percent',
      })

      if (isDw2Repaint()) {
        return {
          value: `${paymentTimes}`,
          primaryText: `${
            paymentTimes < 2
              ? i18n(`installmentPlans_one.optionText`, {
                  paymentTimes,
                  paymentAmount,
                })
              : i18n(`installmentPlans_many.optionText`, {
                  paymentTimes,
                  paymentAmount,
                })
          }
          ${discountPercent &&
            `${i18n('installmentPlans.optionDiscountText', {
              discountWithPercent: normalizedAmount,
              discountPercent,
              valueSymbol: '%',
            })}`}`,
        }
      }

      return (
        <option key={paymentTimes} id={paymentTimes} value={paymentTimes}>
          {paymentTimes < 2
            ? i18n(`installmentPlans_one.optionText`, {
                paymentTimes,
                paymentAmount,
              })
            : i18n(`installmentPlans_many.optionText`, {
                paymentTimes,
                paymentAmount,
              })}
          {discountPercent &&
            `${i18n('installmentPlans.optionDiscountText', {
              discountWithPercent: normalizedAmount,
              discountPercent,
              valueSymbol: '%',
            })}`}
        </option>
      )
    })
  }

  renderCVVInfo = () => {
    const {
      selectedFundingOption: { funding_sources, isRTR },
      cardSpecs,
      language,
    } = this.props
    const showENSubTextRTR = isRTR === true && language !== 'en'
    const fundingSourcesSansCards = funding_sources.filter(
      source => source.instrument_type === INSTRUMENT_TYPES.PAYMENT_CARD
    )
    const { fi_last_4_content = '' } = fundingSourcesSansCards[0]

    const cardType = this.getPaymentCardType(fundingSourcesSansCards[0])
    // Setting the default case to VISA
    const cscSpec = _.get(
      cardSpecs,
      ['cardTypes', cardType, 'fields', 'csc'],
      cardSpecs.cardTypes.VISA.fields.csc
    )

    const cvvComponentClasses = classnames(
      'row',
      'clearfix',
      css(styles.cvvComponent)
    )
    const cvvInfoClasses = classnames(
      'col-xs-6',
      css(styles.cvvInfo),
      css(styles.rtrcvvInfo)
    )
    const cvvInputLogoFrontClasses = css(
      styles.cvvInputLogoFront,
      styles.cvvInputLogo
    )
    const cvvInputLogoBackClasses = css(
      styles.cvvInputLogo,
      styles.cvvInputLogoBack
    )

    const cvvInputLogoClasses =
      cardType === cardSpecTypesToCardTypeConstants.amex
        ? cvvInputLogoFrontClasses
        : cvvInputLogoBackClasses

    const cvvLabelMessage =
      cardType === cardSpecTypesToCardTypeConstants.amex
        ? i18n('label.cvvLabelAmex')
        : i18n('label.cvvLabel')
    const cvvHelptext =
      cardType === cardSpecTypesToCardTypeConstants.amex
        ? i18n('label.cvvHelptextAmex')
        : i18n('label.cvvHelptext')

    const ToolTipTextEnRtr = glamorous.div({
      color: v2grey600,
      marginTop: '8px',
    })

    const CvvInputWrapper = glamorous.div({
      marginTop: '1.5em',
    })

    const toolTipDescription = () => {
      return (
        <>
          <BodyText>{i18n('label.cvvHelperText')}</BodyText>
          {showENSubTextRTR && (
            <CaptionText as={ToolTipTextEnRtr}>
              {enRTRStrings['label.cvvHelperText']}
            </CaptionText>
          )}
        </>
      )
    }

    return (
      <>
        {isDw2Repaint() ? (
          <CvvInputWrapper>
            <TextInputV2
              value={this.props.cvv}
              type="password"
              id="cvvValue"
              data-nemo="cvv-value"
              name="cvvValue"
              label={cvvLabelMessage}
              maxLength={cscSpec.maxlength}
              pattern={cscSpec.pattern}
              required={cscSpec.required}
              onChange={this.handleCVVChange}
              key={fi_last_4_content}
              rightIcon={
                <Tooltip
                  name="cvvValue-tooltip"
                  description={toolTipDescription()}
                  imagePosition="top-left"
                  imageElement={
                    <img
                      alt=""
                      src={`${CDN_HOST_NAME}/paypal-ui/illustrations/focused/svg/csc-back.svg`}
                      style={{ width: '4.25rem' }}
                    />
                  }
                />
              }
            />
          </CvvInputWrapper>
        ) : (
          <div className={cvvComponentClasses}>
            <span className="col-xs-4">
              <TextInput
                value={this.props.cvv}
                type="password"
                id="cvvValue"
                data-nemo="cvv-value"
                name="cvvValue"
                className="vx_form-control vx_form-control_complex cvvInput"
                label={cvvLabelMessage}
                helpText={cvvHelptext}
                maxLength={cscSpec.maxlength}
                pattern={cscSpec.pattern}
                required={cscSpec.required}
                onChange={this.handleCVVChange}
                key={fi_last_4_content}
              />
              <label htmlFor="cvvValue" className={cvvInputLogoClasses} />
            </span>
            <span className={cvvInfoClasses}>{i18n('label.cvvText')}</span>
            {showENSubTextRTR && (
              <span className={cvvInfoClasses}>
                {enRTRStrings['label.cvvText']}
              </span>
            )}
          </div>
        )}
      </>
    )
  }

  renderUnavailableFIWarning = () => {
    const { shouldShowFundingOptionsOverpanel } = this.props
    // Do not show the inline warning when the FI selection overpanel is opened.
    if (shouldShowFundingOptionsOverpanel) {
      return null
    }
    return (
      <OpenBankingWarning
        contentKey="warning.OB.paymentMethodUnavailable"
        style={{
          marginBottom: '20px', // same as <hr/> element that separates the FIs
        }}
      />
    )
  }

  renderYouAreSendingWith = labelContentKey => {
    const {
      selectedFundingOption: { isRTR },
      fundingOptions = [],
      language,
    } = this.props

    // disable toggleFundingOptions if addFI is false and only one funding option
    const disableToggleFundingOptions =
      !this.shallAllowAddFI() && fundingOptions.length === 1
    const showENSubTextRTR = isRTR === true && language !== 'en'
    const labelClassNames = classnames('ppvx_text--md', {
      [css(styles.rtrPrimaryText)]: showENSubTextRTR,
    })

    const labelKey = (
      <div className={labelClassNames}>{i18n(labelContentKey)}</div>
    )
    return isDw2Repaint() ? (
      <SendingWithDivWrapper>
        <div>
          <BodyText>{i18n(labelContentKey)}</BodyText>
          {showENSubTextRTR && (
            <CaptionText style={{ color: v2grey600 }}>
              {enRTRStrings[labelContentKey]}
            </CaptionText>
          )}
        </div>
        {!disableToggleFundingOptions && (
          <Button
            tertiary={true}
            type="button"
            style={{ padding: 0 }}
            id="selectedFundingOptionYourePayingWith"
            onClick={this.handleToggleFundingOptions}
            aria-label={`${i18n('anchor.toggleFISidepanel')}`}
          >
            {i18n('label.toggleFISidepanel')}
          </Button>
        )}
      </SendingWithDivWrapper>
    ) : (
      <SendingWithDiv showENSubTextRTR={showENSubTextRTR}>
        {showENSubTextRTR ? (
          <div>
            {labelKey}
            {showENSubTextRTR && (
              <div className={css(styles.rtrENSubText)}>
                {enRTRStrings[labelContentKey]}
              </div>
            )}
          </div>
        ) : (
          labelKey
        )}
        {!disableToggleFundingOptions && (
          <ToggleButton
            id="selectedFundingOptionYourePayingWith"
            onClick={this.handleToggleFundingOptions}
            type="button"
            aria-label={`${i18n('anchor.toggleFISidepanel')}`}
          >
            <ChangeTextOrIcon
              text={i18n('label.toggleFISidepanel')}
              hideChevron={disableToggleFundingOptions}
            />
          </ToggleButton>
        )}
      </SendingWithDiv>
    )
  }

  renderCurrencyConversionInfo = () => {
    const {
      selectedFundingOption: { isRTR },
      language,
    } = this.props

    const showENSubTextRTR = isRTR === true && language !== 'en'
    const currencyConvertorKey =
      this.props.conversionType === 'EXTERNAL'
        ? 'currency.conversion.cardIssuer'
        : 'currency.conversion.paypal'
    const currencyConvertor = (
      <span className={css(styles.rtrPrimaryText, styles.rtrPrimaryTextSmall)}>
        {i18n(currencyConvertorKey)}
      </span>
    )

    return isDw2Repaint() ? (
      <>
        <Convertor showENSubTextRTR={showENSubTextRTR}>
          <div>
            <BodyText>{i18n(currencyConvertorKey)}</BodyText>
            {showENSubTextRTR && (
              <CaptionText style={{ color: v2grey600 }}>
                {enRTRStrings[currencyConvertorKey]}
              </CaptionText>
            )}
          </div>
          <Button
            type="button"
            tertiary={true}
            style={{ padding: 0 }}
            name="currencyConversionOption"
            data-nemo="currency-conversion-option"
            data-pagename="main:consumer:p2p:transfer:currencyConversionOption"
            onClick={this.handleCurrencyConversionClick}
            aria-label={`${i18n(
              'currency.conversion.change'
            )} ${currencyConvertor}`}
          >
            {i18n('currency.conversion.change')}
          </Button>
        </Convertor>
        {this.props.currencyConversionComponent}
      </>
    ) : (
      <div>
        <Convertor showENSubTextRTR={showENSubTextRTR}>
          <div className="ppvx_text--md">
            {showENSubTextRTR ? (
              <>
                <div className={css(styles.rtrPrimaryText)}>
                  {currencyConvertor}
                </div>
                <div className={css(styles.rtrENSubText)}>
                  {enRTRStrings[currencyConvertorKey]}
                </div>
              </>
            ) : (
              currencyConvertor
            )}
          </div>
          <button
            className="vx_btn-link"
            type="button"
            name="currencyConversionOption"
            data-nemo="currency-conversion-option"
            data-pagename="main:consumer:p2p:transfer:currencyConversionOption"
            onClick={this.handleCurrencyConversionClick}
            aria-label={`${i18n(
              'currency.conversion.change'
            )} ${currencyConvertor}`}
          >
            <ChangeTextOrIcon text={i18n('currency.conversion.change')} />
          </button>
        </Convertor>
        {this.props.currencyConversionComponent}
      </div>
    )
  }

  renderSelectedFundingOptionInfo = () => {
    const {
      selectedFundingOption: {
        funding_sources: [fundingSource = {}] = [],
        showFeeIsIncluded,
        installPlan,
        isRTR,
      },
      selectedInstallmentPlan: { paymentTimes },
      language,
      showStateMandatedWarning,
      isDonate,
    } = this.props

    const showENSubTextRTR = isRTR === true && language !== 'en'
    let feeContent
    let feeContentENSubTextRTR
    let feeClass = ''
    if (fundingSource.subtype === 'ECHECK') {
      const estimatedFundsArrival =
        fundingSource && fundingSource.estimated_funds_arrival
      feeContent = i18n.html('copy.echek', { estimatedFundsArrival })
      feeContentENSubTextRTR = (
        <>
          {enRTRStrings['copy.echek']}{' '}
          <span className="estimatedFundsArrival">{estimatedFundsArrival}</span>
        </>
      )
      feeClass = 'echek'
    } else if (fundingSource.subtype === 'INSTANT_ACH') {
      feeContent = i18n('copy.iach')
      feeContentENSubTextRTR = enRTRStrings['copy.iach']
      feeClass = 'iach'
    } else if (showFeeIsIncluded) {
      feeContent = i18n.html('copy.feeIsIncluded')
      feeContentENSubTextRTR = enRTRStrings['copy.feeIsIncluded']
      feeClass = 'feeIsIncluded'
    }

    const installmentPlansDropdownClasses = classnames(
      'vx_form-control',
      css(styles.installmentPlanDropdown)
    )
    const labelContentKeyDonate = this.props.isRecurring
      ? 'label.youreDonatingRecurringWith'
      : 'label.youreDonatingWith'
    const labelContentKey = this.props.showDonateContent
      ? labelContentKeyDonate
      : 'label.yourePayingWith'

    const selectedFundingOptionClass = classnames(
      'selectedFundingOption-details row',
      css(styles.selectedFundingOption)
    )

    return (
      <div>
        <div className={selectedFundingOptionClass}>
          {this.renderYouAreSendingWith(labelContentKey)}

          <div style={{ marginTop: 25 }}>
            {this.renderFundingSources()}
            {this.props.selectedFundingOption.showCvv && this.renderCVVInfo()}
            {isDw2Repaint() ? (
              <div data-nemo="fee-content" style={{ marginTop: '1rem' }}>
                <CaptionText>{feeContent}</CaptionText>
                {showENSubTextRTR && (
                  <CaptionText style={{ color: v2grey600 }}>
                    {feeContentENSubTextRTR}
                  </CaptionText>
                )}
              </div>
            ) : (
              <div
                data-nemo="fee-content"
                className="funding-option-content row"
              >
                <div className="col-xs-12">
                  <div
                    className={classnames(feeClass, {
                      [css(styles.rtrFundingFeeContent)]: showENSubTextRTR,
                    })}
                  >
                    {feeContent}
                  </div>
                  {showENSubTextRTR && (
                    <div
                      className={classnames(
                        feeClass,
                        css(styles.rtrFundingFeeENSubContent)
                      )}
                    >
                      {feeContentENSubTextRTR}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {installPlan &&
            (isDw2Repaint() ? (
              <>
                <DropdownMenu
                  required={true}
                  id="installmentPlan"
                  label={i18n('installmentPlans.prompt')}
                  options={this.renderDropdownOptions()}
                  value={`${paymentTimes}`}
                  onChange={this.handleChangeInstallmentPlan}
                />
              </>
            ) : (
              <div>
                {/* disabling linting requirement, but should be up to the dev what is needed */
                /* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                  id="installmentPlan"
                  className={installmentPlansDropdownClasses}
                  required={true}
                  value={paymentTimes}
                  onChange={this.handleChangeInstallmentPlan}
                >
                  <option value="-1">{i18n('installmentPlans.prompt')}</option>
                  {this.renderDropdownOptions()}
                </select>
              </div>
            ))}
        </div>
        {showStateMandatedWarning && (
          <StateMandatedWarning>
            <StateMandatedWarningPrimaryText
              showENSubTextRTR={showENSubTextRTR}
            >
              <CaptionText>
                {i18n('disclosure.stateMandatedWarning')}
              </CaptionText>
            </StateMandatedWarningPrimaryText>
            {showENSubTextRTR && (
              <CaptionText as={StateMandatedWarningEnRtrText}>
                {enRTRStrings['disclosure.stateMandatedWarning']}
              </CaptionText>
            )}
          </StateMandatedWarning>
        )}
        {!isDonate && <Separator style={{ marginBottom: '8px' }} />}
        {isBFXCurrencyConversionTrmt() &&
          this.props.shouldShowCurrencyConversionLink &&
          this.renderCurrencyConversionInfo()}
        {this.renderSendingAmountRow()}
        {this.renderFeesRow()}
      </div>
    )
  }

  handleCurrencyConversionClick = event => {
    event.preventDefault()
    this.props.dispatch(
      OverpanelActions.toggleCurrencyConversionOverpanel({ show: true })
    )
  }

  handleChangeInstallmentPlan = event => {
    const paymentTimes = event.target.value
    this.props.dispatch(ViewActions.changeInstallmentPlan(paymentTimes))
  }

  render() {
    const {
      reviewInfoComponent,
      currencyConversionComponent,
      conversionType,
      selectedFundingOption: { displayAmounts = {}, isRTR },
      language,
      additionalDisclosureActions = [],
    } = this.props

    let wrapperCSS = this.props.showCondensedUI
      ? css(styles.wrapper, styles.wrapperCondensed)
      : css(styles.wrapper)

    const wrapperClasses = classnames(
      'preview-fundingOptions-wrapper',
      wrapperCSS
    )

    const showENSubTextRTR = isRTR === true && language !== 'en'

    const currencyConversionContainerClasses = classnames(
      'clearfix',
      css(styles.currencyConversionLinkContainer)
    )

    const currencyConversionLinkDescriptionClasses = classnames(
      'col-xs-10',
      css(styles.currencyConversionDescription)
    )
    const currencyConversionLinkClasses = classnames(
      'col-xs-2',
      'txtRight',
      css(styles.currencyConversionLink),
      'vx_btn-link'
    )
    const currencyConversionLinkDescriptionKey =
      conversionType === 'EXTERNAL'
        ? 'currency.conversion.card'
        : 'currency.conversion.paypal'

    const thirdPartyFeeClasses = classnames(
      css(styles.thirdParty),
      { [css(styles.rtrThirParty)]: showENSubTextRTR },
      'thirdPartyDisclosure'
    )

    return (
      <Wrapper
        className={wrapperClasses}
        ref={f => (this._fundingOption = f)}
        tabIndex="-1"
      >
        {this.props.unavailableFI && this.renderUnavailableFIWarning()}
        <div
          className="selectedFundingOption"
          style={{ ...(isDw2Repaint() && { marginBottom: 0 }) }}
        >
          {Object.keys(displayAmounts).length === 0 &&
            this.renderAddFundingOptionLink()}
          {Object.keys(displayAmounts).length > 0 &&
            this.renderSelectedFundingOptionInfo()}
        </div>

        {Object.keys(displayAmounts).length > 0 && (
          <div>{reviewInfoComponent}</div>
        )}
        {additionalDisclosureActions.includes('FOREIGN_TAX_DEDUCTION') &&
          (isDw2Repaint() ? (
            <AdditionalDisclosureActionsDiv>
              <BodyText>{i18nRTR('rtr.thirdPartyFees')}</BodyText>
              {showENSubTextRTR && (
                <CaptionText style={{ color: v2grey600 }}>
                  {enRTRStrings['rtr.thirdPartyFees']}
                </CaptionText>
              )}
            </AdditionalDisclosureActionsDiv>
          ) : (
            <>
              <p className={thirdPartyFeeClasses}>
                {i18nRTR('rtr.thirdPartyFees')}
              </p>
              {showENSubTextRTR && (
                <p className={css(styles.rtrThirPartyInfoENSubText)}>
                  {enRTRStrings['rtr.thirdPartyFees']}
                </p>
              )}
            </>
          ))}
        {!isBFXCurrencyConversionTrmt() &&
          this.props.shouldShowCurrencyConversionLink &&
          (isDw2Repaint() ? (
            <CurrencyConversionLinkDescriptionDiv>
              <div>
                <BodyText>
                  {i18n(currencyConversionLinkDescriptionKey)}
                </BodyText>
                {showENSubTextRTR && (
                  <CaptionText style={{ color: v2grey600 }}>
                    {enRTRStrings[currencyConversionLinkDescriptionKey]}
                  </CaptionText>
                )}
              </div>
              <Button
                tertiary={true}
                type="button"
                style={{ padding: 0 }}
                name="currencyConversionOption"
                data-nemo="currency-conversion-option"
                data-pagename="main:consumer:p2p:transfer:currencyConversionOption"
                onClick={this.handleCurrencyConversionClick}
              >
                {i18n('currency.conversion.change')}
              </Button>
            </CurrencyConversionLinkDescriptionDiv>
          ) : (
            <div className={currencyConversionContainerClasses}>
              <div className={currencyConversionLinkDescriptionClasses}>
                {showENSubTextRTR ? (
                  <>
                    <div className={css(styles.rtrConversionText)}>
                      {i18n(currencyConversionLinkDescriptionKey)}
                    </div>
                    <div className={css(styles.rtrConversionENSubText)}>
                      {enRTRStrings[currencyConversionLinkDescriptionKey]}
                    </div>
                  </>
                ) : (
                  <span className={css(styles.rtrPrimaryTextSmall)}>
                    {i18n(currencyConversionLinkDescriptionKey)}
                  </span>
                )}
              </div>
              <button
                type="button"
                className={currencyConversionLinkClasses}
                name="currencyConversionOption"
                data-nemo="currency-conversion-option"
                data-pagename="main:consumer:p2p:transfer:currencyConversionOption"
                onClick={this.handleCurrencyConversionClick}
              >
                {i18n('currency.conversion.change')}
              </button>
            </div>
          ))}

        {isBFXCurrencyConversionTrmt() ||
          (isDw2Repaint() ? (
            <>{currencyConversionComponent}</>
          ) : (
            <div className="currencyConversion clearfix">
              {currencyConversionComponent}
            </div>
          ))}
      </Wrapper>
    )
  }
}

export default connect(
  ({ unilateral, transaction, recipient, user, server, overpanels, note }) => ({
    countryCode: user.country,
    unilateralName: unilateral.name,
    unilateralCountryCode: unilateral.countryCode,
    token: recipient.token,
    displayName: recipient.displayName,
    note: note.note,
    thumbnailUrl: recipient.thumbnailUrl,
    networkId: recipient.networkId,
    unavailableFI: transaction.unavailableFI,
    additionalDisclosureActions: transaction.additionalDisclosureActions,
    overpanelType: recipient.moneyPageOverPanel,
    mayCashAdvanceFeesApply: recipient.mayCashAdvanceFeesApply,
    isPurchase: transaction.isPurchase,
    isMgm: server.isMgm,
    griffinMetadata: server.griffinMetadata,
    shouldShowFundingOptionsOverpanel: overpanels.fundingOptionsOverpanel,
    flowIntent: server.flowIntent,
    showStateMandatedWarning: transaction.showStateMandatedWarning,
    isDonate: server.env === 'donate',
    shopsInformation: {
      ...((server.env === FLOW_TYPES.PPSHOPS && {
        payerId: recipient.payerId,
        productId: recipient.productId,
        productQuantity: recipient.productQuantity,
      }) ||
        {}),
    },
    isSingleProductShops:
      server.env === FLOW_TYPES.PPSHOPS &&
      transaction.items &&
      Array.isArray(transaction.items) &&
      transaction.items.length === 1,
  })
)(SelectedFundingOption)
